/* eslint-disable max-classes-per-file */

import { ContractType } from './Models';
import { IFilterOption } from './Models/Filters';

export class ApiUrls {

    // ReSharper disable InconsistentNaming

    public static readonly ErrorUrl: string = '/api/error';
    public static readonly PermissionUrl: string = '/api/permission';
    public static readonly PersonUrl: string = '/api/person';
    public static readonly PersonDeleteUrl: string = '/api/person/delete';
    public static readonly PersonInfoUrl: string = '/api/person/info';
    public static readonly PersonLookupUrl: string = '/api/person/lookup';
    public static readonly PersonFunctionUrl: string = '/api/person/function';
    public static readonly PersonFunctionOverruleUrl: string = '/api/person/function/overrule';
    public static readonly PersonPlaningAutonomyUrl: string = '/api/person/planingautonomy';
    public static readonly PersonContactsUrl: string = '/api/person/contacts';
    public static readonly PersonContractUrl: string = '/api/person/contract';
    public static readonly PersonLicenseUrl: string = '/api/person/license';
    public static readonly PersonLicenseInactivationUrl: string = '/api/person/license/inactivation';
    public static readonly PersonReportUrl: string = '/api/person/report';
    public static readonly EmployeeSummaryReportUrl: string = '/api/employee-summary-report';
    public static readonly SeniorityReportUrl: string = '/api/seniorityreport';
    public static readonly PersonNationalitiestUrl: string = '/api/country';
    public static readonly PersonFleetUrl: string = '/api/person-fleet';
    public static readonly PersonFleetOverrideUrl: string = '/api/person-fleet/override';

    public static readonly FunctionUrl: string = '/api/function';

    public static readonly SignalRUserUrl: string = '/api/signalruser/';

    public static readonly ServiceQueueStatisticsUrl: string = '/api/service/input-queue-statistics';
    public static readonly MessageStatisticsUrl: string = '/api/service/message-statistics';
    public static readonly ServiceDownloadLogsUrl: string = '/api/service/download-logs';
    public static readonly ServiceDownloadFullLogsUrl: string = '/api/service/download-full-logs';
    public static readonly ServiceDownloadDayLogsUrl: string = '/api/service/download-day-logs';
    public static readonly ServiceDatesWithLogsUrl: string = '/api/service/dates';
    
    public static readonly ServiceDownloadDeadlettersUrl: string = '/api/service/download-deadletters';
    public static readonly ServiceQueueDeadlettersUrl: string = '/api/service/input-queue-deadletters';

    public static readonly MonitoringUrl: string = '/api/monitoring';

    public static readonly SelfTestJobsUrl: string = '/api/selftest/jobs';
    public static readonly SelfTestSignalRUrl: string = '/api/selftest/signalr';
    public static readonly SelfTestDatabaseUrl: string = '/api/selftest/database';
    public static readonly SelfTestPersonsQueue: string = '/api/selftest/person-queue';

    public static readonly JobList: string = '/api/job/list';
    public static readonly JobStartUrl: string = '/api/job/start';
    public static readonly JobParametersUrl: string = '/api/job/parameters';
    public static readonly JobTriggerCreateUrl: string = '/api/job/create';
    public static readonly JobTriggerDeleteUrl: string = '/api/job/delete';

    public static readonly ConfigurationUrl: string = '/api/configuration';
    public static readonly ConfigurationFeatureUrl: string = '/api/configuration/feature';

    public static readonly ServiceRestartSchedulerUrl: string = '/api/service/restart-scheduler';
    public static readonly ServiceOperationsUrl: string = '/api/service/operations';

    public static readonly AirportUrl: string = 'api/airport';
    public static readonly DestinationRestrictionGroups: string = '/api/destination-restriction/group';
    public static readonly DestinationRestrictionItems: string = '/api/destination-restriction/group-item';
    public static readonly PersonDestinationRestrictionUrl: string = '/api/destination-restriction/person';
    public static readonly PersonDestinationRestrictionGroupUrl: string = '/api/destination-restriction/person-group';

    public static readonly ConfigurationGlobalUrl: string = '/api/configuration/global';
    public static readonly ChangeLogUrl: string = 'api/changelog';
}

export class Status {
    public static readonly success: string = 'Success';
    public static readonly warning: string = 'Warning';
    public static readonly fail: string = 'Fail';
}

export class Logs {
    public static readonly MaxSizeInMbToDownload: number = 10;
}

export class ComponentUrls {
    public static readonly HubUrl: string = '/hubs/notification';
    public static readonly HubTokenQueryString: string = '?bearerToken=';
}

export class KeyboardKeys {
    public static readonly ENTER: number = 13;
    public static readonly ESCAPE: number = 27;
    public static readonly COMMA: number = 188;
    public static readonly BACKSPACE: number = 8;
}

export class DateTime {
    public static readonly jsonDateFormat: string = 'YYYY-MM-DD';
    public static readonly viewDateFormat: string = 'DD.MM.YYYY';
    public static readonly jsonFullFormat: string = 'YYYY-MM-DD HH:mm:ss';
    public static readonly jsonDateTimeFormat: string = 'YYYY-MM-DDTHH:mm:ssZ';
    public static readonly viewFullFormat: string = 'DD.MM.YYYY HH:mm';
    public static readonly momentFullFormat: string = 'DD.MM.YYYY HH:mm:ss';
    public static readonly monthPeriodFormat: string = 'YYYY M';
    public static readonly viewMonthFormat: string = 'MM.YYYY';
    public static readonly monthPeriodFullFormat: string = 'YYYY-MM';
    public static readonly timeFormat: string = 'HH:mm';
    public static readonly minDate: string = '01.01.2017';
    public static readonly maxDate: string = '31.12.2099';
    public static readonly toStringDateFormat: string = 'MMM DD YYYY';
    public static readonly gridDateFormat: string = 'dd.MM.yyyy';
}

export class Sizes {
    public static readonly gridVirtualSelectMinWidth: number = 180;
    public static readonly maxPersonImageSize: number = 1024 * 1024 * 5;
}

export class PrincipalConfig {
    public static readonly PersonFilterKey: string = 'person';
    public static readonly PersonFilterGroup: string = 'filter';
    public static readonly ReportFiltersKey: string = 'reportFilters';
    public static readonly ReportGridConfigKey: string = 'reportGridConfig';
    public static readonly ReportGridStateKey: string = 'reportGridState';

    public static readonly PersonReportGroup: string = 'personReport';
    public static readonly SeniorityReportGroup: string = 'seniorityReport';
    public static readonly EmployeeSummaryReportGroup: string = 'employeeSummaryReport';
}

export class PartTimeTzv {
    public static readonly minOffLength: number = 12;
    public static readonly maxOffLength: number = 168;
}

export class ParameterType {
    public static readonly DateTime: string = 'DateTime';
    public static readonly Date: string = 'Date';
    public static readonly Boolean: string = 'Boolean';
    public static readonly MultiLine: string = 'Multiline';
    public static readonly SingleLine: string = 'String';
    public static readonly Integer: string = 'Integer';
    public static readonly Enum: string = 'Enum';
}

export class PersonFunctions {
    public static readonly PIC: string = 'PIC';
    public static readonly FO: string = 'FO';
    public static readonly SC: string = 'SC';
    public static readonly CC: string = 'CC';
}

export class FunctionOverruleType {
    public static readonly PIC: string = 'PIC';
    public static readonly PICA_320: string = 'PICA_320';
    public static readonly PICA_330: string = 'PICA_330';
    public static readonly PICA_340: string = 'PICA_340';
    public static readonly PICA_350: string = 'PICA_350';
    public static readonly PICN_320: string = 'PICN_320';
    public static readonly PICN_330: string = 'PICN_330';
    public static readonly PICN_340: string = 'PICN_340';
    public static readonly PICN_350: string = 'PICN_350';
    public static readonly FO: string = 'FO';
    public static readonly FOA_320: string = 'FOA_320';
    public static readonly FOA_330: string = 'FOA_330';
    public static readonly FOA_340: string = 'FOA_340';
    public static readonly FOA_350: string = 'FOA_350';
    public static readonly FON_320: string = 'FON_320';
    public static readonly FON_330: string = 'FON_330';
    public static readonly FON_340: string = 'FON_340';
    public static readonly FON_350: string = 'FON_350';
    public static readonly SC: string = 'SC';
    public static readonly SCA_320: string = 'SCA_320';
    public static readonly SCA_334: string = 'SCA_334';
    public static readonly SCA_345: string = 'SCA_345';
    public static readonly CC: string = 'CC';
    public static readonly CCA_320: string = 'CCA_320';
    public static readonly CCA_334: string = 'CCA_334';
    public static readonly CCA_345: string = 'CCA_345';
}

export class PersonPlaningAutonomyType {
    public static readonly CockpitKat1: string = 'FCM – Kat 1: FlightNr, FlightDays, Office (Fix, Flex), Off';
    public static readonly CockpitKat2: string = 'FCM – Kat 2: FlightNr, FlightDays, Office (Fix, Flex)';
    public static readonly CockpitKat3: string = 'FCM – Kat 3: Office (Fix, Flex)';
    public static readonly CabinPlanningAutonomy: string = 'CCM – Kat 1: Planning Autonomy';
    public static readonly CabinOffice: string = 'CCM – Kat 2: Office';
}

export class Filters {
    public static readonly Date: string = 'date';
}

export class FilterOperation {
    public static readonly notEmpty: string = 'notEmpty';
    public static readonly empty: string = 'empty';
    public static readonly lessThanOrEqual: string = 'lessThanOrEqual';
    public static readonly greaterThanOrEqual: string = 'greaterThanOrEqual';
    public static readonly equal: string = 'equal';
}
export class FilterOperationName {
    public static readonly notEmpty: string = 'Not Empty';
    public static readonly empty: string = 'Empty';
    public static readonly lessThanOrEqual: string = 'Lower or equal';
    public static readonly greaterThanOrEqual: string = 'Bigger or equal';
    public static readonly equal: string = 'Equal';
}

export class FilterOptions {
    public static readonly ContractTypes: IFilterOption[] = [
        { value: ContractType.None, label: 'No Contract' },
        { value: ContractType.Fix, label: 'Fix' },
        { value: ContractType.Freelance, label: 'Freelance' },
        { value: ContractType.Fse, label: 'Fse' },
        { value: ContractType.Tzv, label: 'Tzv' }
    ];
}

export class Configurations {
    public static readonly featureGroup: string = 'Feature';

    public static readonly EnablePermissionsColoringKey: string = 'EnablePermissionsColoring';
    public static readonly EnablePermissionsCheckKey: string = 'EnablePermissionsCheck';
}

export class SearchFilters {
    public static readonly firstName: string = 'firstName';
    public static readonly lastName: string = 'lastName';
    public static readonly code: string = 'code';
    public static readonly id: string = 'id';
    public static readonly exactMatch: string = 'exactMatch';
    public static readonly inactiveInclude: string = 'inactiveInclude';
    public static readonly deletedInclude: string = 'deletedInclude';
}

export const roleMapping: { [key: string]: string } = {
    'IT': 'IT - External',
    'HumanResources': 'Human Resources',
    'CockpitPlanning': 'Cockpit Planning',
    'CabinPlanning': 'Cabin Planning',
    'CrewOffice': 'Crew Office',
    'CockpitLine': 'Cockpit Line',
    'CabinLine': 'Cabin Line',
    'CockpitTraining': 'Cockpit Training',
    'CabinTraining': 'Cabin Training'
};

export class Permissions {
    public static readonly ViewPersonInfoBlock: string = 'ViewPersonInfoBlock';
    public static readonly ViewIdentificationBlock: string = 'ViewIdentificationBlock';
    public static readonly ViewEmploymentFunctionBlock: string = 'ViewEmploymentFunctionBlock';
    public static readonly ViewLicenseBlock: string = 'ViewLicenseBlock';
    public static readonly ViewPlanningBlock: string = 'ViewPlanningBlock';
    public static readonly ViewPersonDestinationRestrictionBlock: string = 'ViewPersonDestinationRestrictionBlock';
    public static readonly ViewIntranetBlock: string = 'ViewIntranetBlock';
    public static readonly ViewHealthBoard: string = 'ViewHealthBoard';
    public static readonly ViewJobList: string = 'ViewJobList';
    public static readonly ViewPersonReport: string = 'ViewPersonReport';
    public static readonly ViewSeniorityReport: string = 'ViewSeniorityReport';
    public static readonly ViewEmployeeSummaryReport: string = 'ViewEmployeeSummaryReport';
    public static readonly ViewPersonLog: string = 'ViewPersonLog';
    public static readonly ViewPersonsList: string = 'ViewPersonsList';
    public static readonly CanDeletePerson: string = 'CanDeletePerson';
    public static readonly CanImpersonate: string = 'CanImpersonate';
    public static readonly CanImpersonateAsAdmin: string = 'CanImpersonateAsAdmin';
    public static readonly CanSendToMessageBus: string = 'CanSendToMessageBus';
    public static readonly ViewObjectPersonContractMainPart: string = 'ViewObjectPersonContractMainPart';
    public static readonly EditObjectPersonContractMainPart: string = 'EditObjectPersonContractMainPart';
    public static readonly ViewAddObjectPersonContractMainPart: string = 'ViewAddObjectPersonContractMainPart';
    public static readonly ViewObjectPersonContractTZVPart: string = 'ViewObjectPersonContractTZVPart';
    public static readonly EditObjectPersonContractTZVPart: string = 'EditObjectPersonContractTZVPart';
    public static readonly ViewObjectPersonFunction: string = 'ViewObjectPersonFunction';
    public static readonly EditObjectPersonFunction: string = 'EditObjectPersonFunction';
    public static readonly ViewAddObjectPersonFunction: string = 'ViewAddObjectPersonFunction';
    public static readonly ViewObjectPersonFunctionOverrule: string = 'ViewObjectPersonFunctionOverrule';
    public static readonly EditObjectPersonFunctionOverrule: string = 'EditObjectPersonFunctionOverrule';
    public static readonly ViewAddObjectPersonFunctionOverrule: string = 'ViewAddObjectPersonFunctionOverrule';
    public static readonly ViewObjectPersonLicenseInactivation: string = 'ViewObjectPersonLicenseInactivation';
    public static readonly EditObjectPersonLicenseInactivation: string = 'EditObjectPersonLicenseInactivation';
    public static readonly ViewAddObjectPersonLicenseInactivation: string = 'ViewAddObjectPersonLicenseInactivation';
    public static readonly ViewObjectPersonPlanningAutonomy: string = 'ViewObjectPersonPlanningAutonomy';
    public static readonly EditObjectPersonPlanningAutonomy: string = 'EditObjectPersonPlanningAutonomy';
    public static readonly ViewAddObjectPersonPlanningAutonomy: string = 'ViewAddObjectPersonPlanningAutonomy';
    public static readonly ViewObjectPersonDestinationRestriction: string = 'ViewObjectPersonDestinationRestriction';
    public static readonly EditObjectPersonDestinationRestriction: string = 'EditObjectPersonDestinationRestriction';
    public static readonly ViewAddObjectPersonDestinationRestriction: string = 'ViewAddObjectPersonDestinationRestriction';
    public static readonly ViewObjectPersonDestinationRestrictionGroup: string = 'ViewObjectPersonDestinationRestrictionGroup';
    public static readonly EditObjectPersonDestinationRestrictionGroup: string = 'EditObjectPersonDestinationRestrictionGroup';
    public static readonly ViewAddObjectPersonDestinationRestrictionGroup: string = 'ViewAddObjectPersonDestinationRestrictionGroup';
    public static readonly ViewMasterGroupItems: string = 'ViewMasterGroupItems';
    public static readonly ViewMasterDataItems: string = 'ViewMasterDataItems';
    public static readonly EditMasterZumErfassenVonGroupItems: string = 'EditMasterZumErfassenVonGroupItems';
    public static readonly ViewAddMasterGroupItems: string = 'ViewAddMasterGroupItems';
    public static readonly ViewObjectPersonFleet: string = 'ViewObjectPersonFleet';
    public static readonly EditObjectPersonFleet: string = 'EditObjectPersonFleet';
    public static readonly ViewObjectPersonFleetOverride: string = 'ViewObjectPersonFleetOverride';
    public static readonly EditObjectPersonFleetOverride: string = 'EditObjectPersonFleetOverride';
    public static readonly ViewAddObjectPersonFleetOverride: string = 'ViewAddObjectPersonFleetOverride';
    public static readonly ViewPersonUsername: string = 'ViewPersonUsername';
    public static readonly EditPersonUsername: string = 'EditPersonUsername';
    public static readonly ViewPersonPicture: string = 'ViewPersonPicture';
    public static readonly EditPersonPicture: string = 'EditPersonPicture';
    public static readonly ViewPersonELSQualified: string = 'ViewPersonELSQualified';
    public static readonly EditPersonELSQualified: string = 'EditPersonELSQualified';
    public static readonly ViewPersonAPISbyEG: string = 'ViewPersonAPISbyEG';
    public static readonly EditPersonAPISbyEG: string = 'EditPersonAPISbyEG';
    public static readonly ViewPersonAeropersIsMember: string = 'ViewPersonAeropersIsMember';
    public static readonly EditPersonAeropersIsMember: string = 'EditPersonAeropersIsMember';
    public static readonly ViewPersonAeropersMemberFrom: string = 'ViewPersonAeropersMemberFrom';
    public static readonly EditPersonAeropersMemberFrom: string = 'EditPersonAeropersMemberFrom';
    public static readonly ViewPersonAeropersMemberTo: string = 'ViewPersonAeropersMemberTo';
    public static readonly EditPersonAeropersMemberTo: string = 'EditPersonAeropersMemberTo';
    public static readonly ViewPersonNationality: string = 'ViewPersonNationality';
    public static readonly EditPersonNationality: string = 'EditPersonNationality';
    public static readonly ViewPersonPassportNumber: string = 'ViewPersonPassportNumber';
    public static readonly EditPersonPassportNumber: string = 'EditPersonPassportNumber';
    public static readonly ViewPersonPassportExpiration: string = 'ViewPersonPassportExpiration';
    public static readonly EditPersonPassportExpiration: string = 'EditPersonPassportExpiration';
    public static readonly ViewPersonSecondNationality: string = 'ViewPersonSecondNationality';
    public static readonly EditPersonSecondNationality: string = 'EditPersonSecondNationality';
    public static readonly ViewPersonSecondPassportNumber: string = 'ViewPersonSecondPassportNumber';
    public static readonly EditPersonSecondPassportNumber: string = 'EditPersonSecondPassportNumber';
    public static readonly ViewPersonSecondPassportExpiration: string = 'ViewPersonSecondPassportExpiration';
    public static readonly EditPersonSecondPassportExpiration: string = 'EditPersonSecondPassportExpiration';
    public static readonly ViewPersonVisaUsaBExpiration: string = 'ViewPersonVisaUsaBExpiration';
    public static readonly EditPersonVisaUsaBExpiration: string = 'EditPersonVisaUsaBExpiration';
    public static readonly ViewPersonVisaUsaCExpiration: string = 'ViewPersonVisaUsaCExpiration';
    public static readonly EditPersonVisaUsaCExpiration: string = 'EditPersonVisaUsaCExpiration';
    public static readonly ViewPersonVisaRussiaExpiration: string = 'ViewPersonVisaRussiaExpiration';
    public static readonly EditPersonVisaRussiaExpiration: string = 'EditPersonVisaRussiaExpiration';
    public static readonly ViewContractDateFrom: string = 'ViewContractDateFrom';
    public static readonly EditContractDateFrom: string = 'EditContractDateFrom';
    public static readonly ViewContractDateTo: string = 'ViewContractDateTo';
    public static readonly EditContractDateTo: string = 'EditContractDateTo';
    public static readonly EditContractDateToIfNotEmpty: string = 'EditContractDateToIfNotEmpty';
    public static readonly ViewContractType: string = 'ViewContractType';
    public static readonly EditContractType: string = 'EditContractType';
    public static readonly ViewContractPartTimePercentage: string = 'ViewContractPartTimePercentage';
    public static readonly EditContractPartTimePercentage: string = 'EditContractPartTimePercentage';
    public static readonly ViewContractOfficePercentage: string = 'ViewContractOfficePercentage';
    public static readonly EditContractOfficePercentage: string = 'EditContractOfficePercentage';
    public static readonly ViewContractTypeModels: string = 'ViewContractTypeModels';
    public static readonly EditContractTypeModels: string = 'EditContractTypeModels';
    public static readonly ViewContractIsFinalized: string = 'ViewContractIsFinalized';
    public static readonly EditContractIsFinalized: string = 'EditContractIsFinalized';
    public static readonly EditContractIsFinalizedWithNotMatchedPercentage: string = 'EditContractIsFinalizedWithNotMatchedPercentage';
    public static readonly ViewContractIsExternalEmployee: string = 'ViewContractIsExternalEmployee';
    public static readonly EditContractIsExternalEmployee: string = 'EditContractIsExternalEmployee';
    public static readonly ViewFunctionContractOverview: string = 'ViewFunctionContractOverview';
    public static readonly ViewContractFlyingDutyPercentage: string = 'ViewContractFlyingDutyPercentage';
    public static readonly ViewContractPartTimeMonthContractDefinition: string = 'ViewContractPartTimeMonthContractDefinition';
    public static readonly EditContractPartTimeMonthContractDefinition: string = 'EditContractPartTimeMonthContractDefinition';
    public static readonly ViewContractPartTimeDayContractDefinition: string = 'ViewContractPartTimeDayContractDefinition';
    public static readonly EditContractPartTimeDayContractDefinition: string = 'EditContractPartTimeDayContractDefinition';
    public static readonly ViewContractPartTimeDaySplitType: string = 'ViewContractPartTimeDaySplitType';
    public static readonly EditContractPartTimeDaySplitType: string = 'EditContractPartTimeDaySplitType';
    public static readonly ViewContractPartTimeVariableBlockLength: string = 'ViewContractPartTimeVariableBlockLength';
    public static readonly EditContractPartTimeVariableBlockLength: string = 'EditContractPartTimeVariableBlockLength';
    public static readonly ViewContractPartTimeVariableBlockPerYear: string = 'ViewContractPartTimeVariableBlockPerYear';
    public static readonly EditContractPartTimeVariableBlockPerYear: string = 'EditContractPartTimeVariableBlockPerYear';
    public static readonly ViewContractPartTimeVariableDayBlockLength: string = 'ViewContractPartTimeVariableDayBlockLength';
    public static readonly EditContractPartTimeVariableDayBlockLength: string = 'EditContractPartTimeVariableDayBlockLength';
    public static readonly ViewContractPartTimeVariableDaySplitType: string = 'ViewContractPartTimeVariableDaySplitType';
    public static readonly EditContractPartTimeVariableDaySplitType: string = 'EditContractPartTimeVariableDaySplitType';
    public static readonly ViewContractPartTimeFixBlockDays: string = 'ViewContractPartTimeFixBlockDays';
    public static readonly EditContractPartTimeFixBlockDays: string = 'EditContractPartTimeFixBlockDays';
    public static readonly ViewContractPartTimeFixBlockFrequency: string = 'ViewContractPartTimeFixBlockFrequency';
    public static readonly EditContractPartTimeFixBlockFrequency: string = 'EditContractPartTimeFixBlockFrequency';
    public static readonly ViewContractPartTimeFixBlockBeginDay: string = 'ViewContractPartTimeFixBlockBeginDay';
    public static readonly EditContractPartTimeFixBlockBeginDay: string = 'EditContractPartTimeFixBlockBeginDay';
    public static readonly ViewContractPartTimeFixBlockBeginMonth: string = 'ViewContractPartTimeFixBlockBeginMonth';
    public static readonly EditContractPartTimeFixBlockBeginMonth: string = 'EditContractPartTimeFixBlockBeginMonth';
    public static readonly ViewContractPartTimeFixDaysPerWeek: string = 'ViewContractPartTimeFixDaysPerWeek';
    public static readonly EditContractPartTimeFixDaysPerWeek: string = 'EditContractPartTimeFixDaysPerWeek';
    public static readonly ViewContractPartTimeFixDayFirstDay: string = 'ViewContractPartTimeFixDayFirstDay';
    public static readonly EditContractPartTimeFixDayFirstDay: string = 'EditContractPartTimeFixDayFirstDay';
    public static readonly ViewContractPartTimeFixDaySecondDay: string = 'ViewContractPartTimeFixDaySecondDay';
    public static readonly EditContractPartTimeFixDaySecondDay: string = 'EditContractPartTimeFixDaySecondDay';
    public static readonly ViewContractPartTimeFixDayReason: string = 'ViewContractPartTimeFixDayReason';
    public static readonly EditContractPartTimeFixDayReason: string = 'EditContractPartTimeFixDayReason';
    public static readonly ViewContractComment: string = 'ViewContractComment';
    public static readonly EditContractComment: string = 'EditContractComment';
    public static readonly ViewEditContractComment: string = 'ViewEditContractComment';
    public static readonly ViewContractPartTimeSpecialDaysPerYear: string = 'ViewContractPartTimeSpecialDaysPerYear';
    public static readonly EditContractPartTimeSpecialDaysPerYear: string = 'EditContractPartTimeSpecialDaysPerYear';
    public static readonly ViewContractPartTimeSpecialVacEntitlement: string = 'ViewContractPartTimeSpecialVacEntitlement';
    public static readonly EditContractPartTimeSpecialVacEntitlement: string = 'EditContractPartTimeSpecialVacEntitlement';
    public static readonly ViewFunctionDateFrom: string = 'ViewFunctionDateFrom';
    public static readonly EditFunctionDateFrom: string = 'EditFunctionDateFrom';
    public static readonly ViewFunctionDateTo: string = 'ViewFunctionDateTo';
    public static readonly EditFunctionDateTo: string = 'EditFunctionDateTo';
    public static readonly ViewFunctionFunctionType: string = 'ViewFunctionFunctionType';
    public static readonly EditFunctionType: string = 'EditFunctionType';
    public static readonly ViewFunctionDateSince: string = 'ViewFunctionDateSince';
    public static readonly ViewFunctionPlanningGroup: string = 'ViewFunctionPlanningGroup';
    public static readonly EditFunctionDateSince: string = 'EditFunctionDateSince';
    public static readonly EditFunctionPlanningGroup: string = 'EditFunctionPlanningGroup';
    public static readonly ViewFunctionFunctionOverrules: string = 'ViewFunctionFunctionOverrules';
    public static readonly ViewPersonFlightLicenseNumber: string = 'ViewPersonFlightLicenseNumber';
    public static readonly ViewPersonFunctionOverruleType: string = 'ViewPersonFunctionOverruleType';
    public static readonly EditPersonFunctionOverruleType: string = 'EditPersonFunctionOverruleType';
    public static readonly ViewPersonFunctionOverruleDateFrom: string = 'ViewPersonFunctionOverruleDateFrom';
    public static readonly EditPersonFunctionOverruleDateFrom: string = 'EditPersonFunctionOverruleDateFrom';
    public static readonly ViewPersonFunctionOverruleDateTo: string = 'ViewPersonFunctionOverruleDateTo';
    public static readonly EditPersonFunctionOverruleDateTo: string = 'EditPersonFunctionOverruleDateTo';
    public static readonly ViewPersonFunctionOverruleComment: string = 'ViewPersonFunctionOverruleComment';
    public static readonly EditPersonFunctionOverruleComment: string = 'EditPersonFunctionOverruleComment';
    public static readonly ViewPersonExpireDateMedical: string = 'ViewPersonExpireDateMedical';
    public static readonly EditPersonExpireDateMedical: string = 'EditPersonExpireDateMedical';
    public static readonly ViewPersonSeniorityBase: string = 'ViewPersonSeniorityBase';
    public static readonly EditPersonSeniorityBase: string = 'EditPersonSeniorityBase';
    public static readonly ViewPersonSeniorityOverride: string = 'ViewPersonSeniorityOverride';
    public static readonly EditPersonSeniorityOverride: string = 'EditPersonSeniorityOverride';
    public static readonly ViewPersonSeniorityComment: string = 'ViewPersonSeniorityComment';
    public static readonly EditPersonSeniorityComment: string = 'EditPersonSeniorityComment';
    public static readonly ViewPersonDosimetryTotal: string = 'ViewPersonDosimetryTotal';
    public static readonly EditPersonDosimetryTotal: string = 'EditPersonDosimetryTotal';
    public static readonly ViewPersonDosimetryYTD: string = 'ViewPersonDosimetryYTD';
    public static readonly EditPersonDosimetryYTD: string = 'EditPersonDosimetryYTD';
    public static readonly ViewPersonSettingsFlightHoursBeforeEdelweiss: string = 'ViewPersonSettingsFlightHoursBeforeEdelweiss';
    public static readonly EditPersonFlightHoursBeforeEdelweiss: string = 'EditPersonFlightHoursBeforeEdelweiss';
    public static readonly ViewLicensesOverview: string = 'ViewLicensesOverview';
    public static readonly ViewPersonLicenseInactivationDateFrom: string = 'ViewPersonLicenseInactivationDateFrom';
    public static readonly EditPersonLicenseInactivationDateFrom: string = 'EditPersonLicenseInactivationDateFrom';
    public static readonly ViewPersonLicenseInactivationDateTo: string = 'ViewPersonLicenseInactivationDateTo';
    public static readonly EditPersonLicenseInactivationDateTo: string = 'EditPersonLicenseInactivationDateTo';
    public static readonly ViewPersonLicenseInactivationComment: string = 'ViewPersonLicenseInactivationComment';
    public static readonly EditPersonLicenseInactivationComment: string = 'EditPersonLicenseInactivationComment';
    public static readonly ViewPlanningAutonomyDateFrom: string = 'ViewPlanningAutonomyDateFrom';
    public static readonly EditPlanningAutonomyDateFrom: string = 'EditPlanningAutonomyDateFrom';
    public static readonly ViewPlanningAutonomyDateTo: string = 'ViewPlanningAutonomyDateTo';
    public static readonly EditPlanningAutonomyDateTo: string = 'EditPlanningAutonomyDateTo';
    public static readonly ViewPlanningAutonomyType: string = 'ViewPlanningAutonomyType';
    public static readonly EditPlanningAutonomyType: string = 'EditPlanningAutonomyType';
    public static readonly ViewPersonDestinationRestrictionAirportCode: string = 'ViewPersonDestinationRestrictionAirportCode';
    public static readonly EditPersonDestinationRestrictionAirportCode: string = 'EditPersonDestinationRestrictionAirportCode';
    public static readonly ViewPersonDestinationRestrictionDateFrom: string = 'ViewPersonDestinationRestrictionDateFrom';
    public static readonly EditPersonDestinationRestrictionDateFrom: string = 'EditPersonDestinationRestrictionDateFrom';
    public static readonly ViewPersonDestinationRestrictionDateTo: string = 'ViewPersonDestinationRestrictionDateTo';
    public static readonly EditPersonDestinationRestrictionDateTo: string = 'EditPersonDestinationRestrictionDateTo';
    public static readonly ViewPersonDestinationRestrictionComment: string = 'ViewPersonDestinationRestrictionComment';
    public static readonly EditPersonDestinationRestrictionComment: string = 'EditPersonDestinationRestrictionComment';
    public static readonly ViewPersonGroupDestinationRestrictionDateFrom: string = 'ViewPersonGroupDestinationRestrictionDateFrom';
    public static readonly EditPersonGroupDestinationRestrictionDateFrom: string = 'EditPersonGroupDestinationRestrictionDateFrom';
    public static readonly ViewPersonGroupDestinationRestrictionDateTo: string = 'ViewPersonGroupDestinationRestrictionDateTo';
    public static readonly EditPersonGroupDestinationRestrictionDateTo: string = 'EditPersonGroupDestinationRestrictionDateTo';
    public static readonly ViewPersonGroupDestinationRestrictionComment: string = 'ViewPersonGroupDestinationRestrictionComment';
    public static readonly EditPersonGroupDestinationRestrictionComment: string = 'EditPersonGroupDestinationRestrictionComment';
    public static readonly ViewCanSeeNonReleasedCASchedule: string = 'ViewCanSeeNonReleasedCASchedule';
    public static readonly EditCanSeeNonReleasedCASchedule: string = 'EditCanSeeNonReleasedCASchedule';
    public static readonly ViewCanSeeNonReleasedCOSchedule: string = 'ViewCanSeeNonReleasedCOSchedule';
    public static readonly EditCanSeeNonReleasedCOSchedule: string = 'EditCanSeeNonReleasedCOSchedule';
    public static readonly ViewCanDownloadBuddySchedules: string = 'ViewCanDownloadBuddySchedules';
    public static readonly EditCanDownloadBuddySchedules: string = 'EditCanDownloadBuddySchedules';
    public static readonly ViewCanSeeAllSchedules: string = 'ViewCanSeeAllSchedules';
    public static readonly EditCanSeeAllSchedules: string = 'EditCanSeeAllSchedules';
    public static readonly ViewPersonFleetDateFrom: string = 'ViewPersonFleetDateFrom';
    public static readonly EditPersonFleetDateFrom: string = 'EditPersonFleetDateFrom';
    public static readonly ViewPersonFleetDateTo: string = 'ViewPersonFleetDateTo';
    public static readonly EditPersonFleetDateTo: string = 'EditPersonFleetDateTo';
    public static readonly ViewPersonFleetType: string = 'ViewPersonFleetType';
    public static readonly EditPersonFleetType: string = 'EditPersonFleetType';
    public static readonly ViewPersonFleetOverrideDateFrom: string = 'ViewPersonFleetOverrideDateFrom';
    public static readonly EditPersonFleetOverrideDateFrom: string = 'EditPersonFleetOverrideDateFrom';
    public static readonly ViewPersonFleetOverrideDateTo: string = 'ViewPersonFleetOverrideDateTo';
    public static readonly EditPersonFleetOverrideDateTo: string = 'EditPersonFleetOverrideDateTo';
    public static readonly ViewPersonFleetOverrideType: string = 'ViewPersonFleetOverrideType';
    public static readonly EditPersonFleetOverrideType: string = 'EditPersonFleetOverrideType';
    public static readonly ViewPersonPictureOriginalDerivations: string = 'ViewPersonPictureOriginalDerivations';
    public static readonly EditJobList: string = 'EditJobList';
    public static readonly ViewObjectPersonContacts: string = 'ViewObjectPersonContacts';
    public static readonly ViewPersonCmcDate: string = 'ViewPersonCmcDate';
    public static readonly EditPersonCmcDate: string = 'EditPersonCmcDate';
    public static readonly ViewPersonEnableNbu: string = 'ViewPersonEnableNbu';
    public static readonly EditPersonEnableNbu: string = 'EditPersonEnableNbu';

}
export class PersonFleetOverrideType {
    public static readonly A320: string = 'A320';
    public static readonly A320_A330: string = 'A320_A330';
    public static readonly A320_A340: string = 'A320_A340';
    public static readonly A330_A340: string = 'A330_A340';
    public static readonly A340: string = 'A340';
    public static readonly A350: string = 'A350';
    public static readonly Cabin: string = 'Cabin';
}